import React, { useContext, useEffect, useState, useMemo, useCallback } from 'react';
import styled, { useTheme } from 'styled-components';
import ReactLoading from 'react-loading';
import { Creative, GridLayout, GridLayoutInput, CreativeInput, CreativeMetricsInput } from 'API';
import { useCustomReportContext } from 'context/CustomReportContext';
import DeleteConfirmationModal from 'components/DeleteConfirmationModall';
import { createPortal } from 'react-dom';
import { fullSources } from 'customHooks/useDataSources';
import { useSaveCustomReportCreative } from './CustomReportsHooks/useCustomReportsCreative';
import { useSaveNewSectionItem, useGetCustomReportPublishedData } from './CustomReportsHooks/useCustomReportsGrid';
import { removeTypename } from 'methods/removeTypename';
import { platformLogoSelector } from 'methods/platformLogoSelector';
import { DarkButton, IconButton } from 'components/style/Global';
import {
  PlatformMidRow,
  BottomRow,
  NavigationButton,
  MidRow,
  Selections,
  RightPane,
} from 'components/Main/table/TableStyles';
import { Modal } from 'components/NewPortal/Header/AddCampaignModal';
import { ModulContainer, HeadModul } from 'components/Main/table/TableStyles';
import { IoMdClose } from 'react-icons/io';
import AuthContext from 'context/AuthContext';
import { prepQuery } from 'methods/prepQuery';
import { useDataQuery } from 'customHooks/useDataQuery';
import { processDataSource } from 'utils/processDataSource';
import { FileUploader } from 'react-drag-drop-files';
import { API } from 'aws-amplify';
import { getPreSignedUrl } from 'graphql/queries';
import { GetPreSignedUrlQuery, S3Method } from 'API';
import { v4 as uuidv4 } from 'uuid';
import APIContext from 'context/APIContext';

// Types
export type CreativeValues = {
  dataSourceName?: string | null;
  creative?: Creative | null;
  data?: any[] | null;
  outputLocation?: string | null;
  title?: string | null;
};

type Props = {
  sectionID: string | null | undefined;
  layoutItem: GridLayout;
  modal?: boolean;
  handleModalType: (item: GridLayout | null, type: string | null) => void;
  deleteItem?: (sectionID: string, sectionItemID: string) => void;
  initialCreative: CreativeValues | null;
  fetchCreative: (sectionItemID: string) => Promise<CreativeValues | null>;
  onCreativeSaved?: (sectionItemID: string, creative: CreativeValues) => void;
};

// ============ STYLED COMPONENTS ============

const CreativeDisplayStyled = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
  padding: 0.4vw;

  &:hover {
    .edit-btns {
      display: flex !important;
    }
  }

  .edit-btns {
    display: none;
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 10;
    flex-direction: row;
    gap: 5px;
    background-color: transparent;

    .edit-btn {
      font-weight: 300;
      box-shadow:
        0 2px 8px rgba(0, 0, 0, 0.1),
        0 1px 3px rgba(0, 0, 0, 0.08);
      border-radius: 50%;
      cursor: pointer;
      padding: 6px;
      transition: all 0.2s ease;
      background: ${({ theme }) => theme.c50};
      color: ${({ theme }) => theme.brand};
      border: 1px solid ${({ theme }) => theme.c100}40;

      &:hover {
        outline: 1px solid ${({ theme }) => theme.c200};
        box-shadow:
          0 3px 12px rgba(0, 0, 0, 0.15),
          0 2px 6px rgba(0, 0, 0, 0.1);
        transform: scale(1.05);
        background: ${({ theme }) => theme.c100};
      }
    }
  }

  .creative-content {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;

    .preview-container {
      flex: 1;
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      border-radius: 6px;

      img,
      video {
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
        border-radius: 6px;
      }

      iframe {
        width: 100%;
        height: 100%;
        border: none;
        border-radius: 6px;
      }
    }

    .creative-name {
      font-size: 12px;
      color: ${({ theme }) => theme.primaryGray};
      text-align: center;
    }

    .metrics-row {
      display: flex;
      gap: 16px;
      flex-wrap: wrap;
      justify-content: center;
      padding: 8px;
      /* background: ${({ theme }) => theme.c50}; */
      border-radius: 4px;
      width: 100%;

      .metric {
        display: flex;
        flex-direction: column;
        align-items: center;

        .label {
          font-size: 10px;
          color: ${({ theme }) => theme.brand};
        }

        .value {
          font-size: 14px;
          font-weight: bold;
          color: ${({ theme }) => theme.primaryGray};
        }
      }
    }
  }

  .empty-state {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    color: #999;
    font-size: 14px;
  }
`;

const ModalContentStyled = styled.div`
  width: 100%;
  min-height: 60vh;

  .step-container {
    width: 100%;
  }

  .loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    gap: 1rem;
    color: ${({ theme }) => theme.primaryGray};
  }

  .editor-container {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
    padding: 20px 0;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  .form-section {
    display: flex;
    flex-direction: column;
    gap: 16px;

    .form-group {
      display: flex;
      flex-direction: column;
      gap: 6px;

      label {
        font-weight: 500;
        font-size: 14px;
        color: ${({ theme }) => theme.primaryGray};
      }

      select,
      input[type='text'] {
        padding: 10px 12px;
        border: 1px solid ${({ theme }) => theme.c200};
        border-radius: 6px;
        font-size: 14px;
        outline: none;

        &:focus {
          border-color: ${({ theme }) => theme.brand};
        }
      }
    }

    .file-drop-zone {
      border: 2px dashed ${({ theme }) => theme.c200};
      border-radius: 8px;
      padding: 24px;
      text-align: center;
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        border-color: ${({ theme }) => theme.brand};
        background: ${({ theme }) => theme.c50};
      }

      p {
        margin: 0;
        color: ${({ theme }) => theme.secondaryGray};
      }
    }

    .preview-box {
      border: 1px solid ${({ theme }) => theme.c200};
      border-radius: 8px;
      padding: 12px;
      background: ${({ theme }) => theme.c50};
      max-height: 200px;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;

      img,
      video {
        max-width: 100%;
        max-height: 180px;
        object-fit: contain;
      }
    }
  }

  .metrics-section {
    .metrics-header {
      font-weight: 500;
      font-size: 14px;
      color: ${({ theme }) => theme.primaryGray};
      margin-bottom: 12px;
    }

    .metrics-list {
      display: flex;
      flex-direction: column;
      gap: 8px;
      max-height: 300px;
      overflow-y: auto;
    }

    .metric-item {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 10px 12px;
      border: 1px solid ${({ theme }) => theme.c200};
      border-radius: 6px;
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        background: ${({ theme }) => theme.c50};
      }

      &.selected {
        background: ${({ theme }) => theme.c100};
        border-color: ${({ theme }) => theme.brand};
      }

      &.disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      input[type='checkbox'] {
        width: 16px;
        height: 16px;
      }

      .metric-name {
        flex: 1;
        font-size: 14px;
      }

      .metric-value {
        font-size: 12px;
        color: ${({ theme }) => theme.secondaryGray};
      }
    }
  }
`;

// ============ COMPONENT ============

const CustomReportCreative: React.FC<Props> = (props) => {
  const {
    sectionID,
    layoutItem,
    modal = false,
    handleModalType,
    deleteItem,
    initialCreative,
    fetchCreative,
    onCreativeSaved,
  } = props;

  // Context
  const {
    activeCustomReport,
    dataSources,
    customReportCampaignList,
    transformedACDetailsReport,
    safeSectionLayoutUpdate,
    setPublishPlaceholderObject,
  } = useCustomReportContext();
  const { user } = useContext(AuthContext);
  const { getEnvironment } = useContext(APIContext);

  // Hooks
  const [saveNewSectionItem] = useSaveNewSectionItem();
  const [saveCreative] = useSaveCustomReportCreative();
  const [publishedData] = useGetCustomReportPublishedData();
  const theme = useTheme();

  const userIsAuthorized = activeCustomReport?.authorized;

  // Get report date filter for prepQuery
  const reportDateFilter = activeCustomReport?.Filters?.find((filter) => filter?.column === 'date');

  // Update publish placeholder object when creative data is available
  useEffect(() => {
    if (initialCreative && layoutItem.SectionItemID) {
      const placeholdersObject = {
        position: String(layoutItem.SectionItemID),
        outputlocation: initialCreative.outputLocation || '',
        outputlocationTotal: 'noTotal',
        title: initialCreative.title || '',
        metrics: {
          chartType: 'creative',
          metric: 'creative',
          axis: 1,
        },
      };
      const IdtoUse = layoutItem.SectionItemID;

      setPublishPlaceholderObject((prev) => ({
        ...prev,
        [IdtoUse || '']: {
          ...prev[IdtoUse || ''],
          ...placeholdersObject,
        },
      }));
    }
  }, [initialCreative, layoutItem.SectionItemID, setPublishPlaceholderObject]);

  // State
  const [step, setStep] = useState<'dataSource' | 'creative'>('dataSource');
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Data source state
  const [selectedDataSource, setSelectedDataSource] = useState<fullSources | null>(null);
  const [fetchedData, setFetchedData] = useState<any[]>([]);
  const [dataOutputLocation] = useState<string>('');

  // Creative editor state
  const [selectedCreativeId, setSelectedCreativeId] = useState<string>('');
  const [selectedCreativeIds, setSelectedCreativeIds] = useState<string[]>([]);
  const [isMultiSelect, setIsMultiSelect] = useState(false);
  const [customCreativeName, setCustomCreativeName] = useState('');
  const [creativeName, setCreativeName] = useState<string>('');
  const [creativeTitle, setCreativeTitle] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState<string>('');
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>([]);

  // Available metrics objects (from data)
  const [availableMetricsObjects, setAvailableMetricsObjects] = useState<
    Array<{
      position: number;
      label: string;
      value: number;
      column: string;
      decimals: number;
    }>
  >([]);

  // Custom metrics state
  const [customMetrics, setCustomMetrics] = useState<Array<{ label: string; value: number; decimals?: number }>>([]);
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingMetric, setEditingMetric] = useState<{
    type: 'data' | 'custom';
    index?: number;
    metricId?: string;
    label: string;
    value: string;
    decimals?: number;
  } | null>(null);
  const [customLabel, setCustomLabel] = useState('');
  const [customValue, setCustomValue] = useState('');
  const [editLabel, setEditLabel] = useState('');
  const [editValue, setEditValue] = useState('');
  const [editDecimals, setEditDecimals] = useState('2');

  // Query for fetching data
  const [userQuery, setUserQuery] = useState<any>(null);
  const [queryData] = useDataQuery({
    query: userQuery,
    querySource: 'QUERY',
  });

  // Filter dataSources - exclude Custom (same as CustomReportQuery)
  const filteredDataSources = useMemo(() => {
    return dataSources.filter((el) => el.name !== 'Custom');
  }, [dataSources]);

  // Check if we're in edit mode (has existing creative data)
  const isEditMode = Boolean(initialCreative?.creative);

  // Available creative IDs from fetched data (as objects with id and label)
  const availableCreativeIds = useMemo(() => {
    if (!fetchedData || fetchedData.length === 0) return [];
    const creativeMap = new Map<string, { id: string; label: string }>();
    fetchedData.forEach((row) => {
      if (row.creative_id) {
        const id = String(row.creative_id);
        if (!creativeMap.has(id)) {
          // Use creative_name if available, otherwise use creative_id
          const label = row.creative_name || row.creativeName || row.name || id;
          creativeMap.set(id, { id, label: String(label) });
        }
      }
    });
    return Array.from(creativeMap.values());
  }, [fetchedData]);

  // Available metrics from fetched data
  const availableMetrics = useMemo(() => {
    if (!fetchedData || fetchedData.length === 0) return [];
    const firstRow = fetchedData[0];
    return Object.keys(firstRow).filter((key) => {
      const val = firstRow[key];
      return typeof val === 'number' || (!isNaN(parseFloat(val)) && key !== 'creative_id');
    });
  }, [fetchedData]);

  // Get metric value for selected creative (supports multi-select)
  const getMetricValue = useCallback(
    (metricKey: string): number => {
      if (!fetchedData) return 0;

      if (isMultiSelect && selectedCreativeIds.length > 0) {
        // For multiselect, sum the metric values
        const matchingRows = fetchedData.filter((r) => selectedCreativeIds.includes(String(r.creative_id)));
        return matchingRows.reduce((sum, row) => {
          const value = row[metricKey];
          return sum + (typeof value === 'number' ? value : parseFloat(value) || 0);
        }, 0);
      } else if (selectedCreativeId) {
        // Single select
        const row = fetchedData.find((r) => String(r.creative_id) === selectedCreativeId);
        return row ? parseFloat(row[metricKey]) || 0 : 0;
      }
      return 0;
    },
    [fetchedData, selectedCreativeId, isMultiSelect, selectedCreativeIds],
  );

  // Effect: Handle query data received
  useEffect(() => {
    if (queryData && Array.isArray(queryData) && queryData.length > 0 && selectedDataSource) {
      setFetchedData(queryData as any[]);
      setIsLoading(false);
      setStep('creative');
    }
  }, [queryData, selectedDataSource]);

  // Effect: Load initial creative for editing
  useEffect(() => {
    if (modal && initialCreative) {
      // Populate state from initialCreative
      if (initialCreative.data) {
        setFetchedData(initialCreative.data);
      }
      if (initialCreative.title) {
        setCreativeTitle(initialCreative.title);
      }
      if (initialCreative.creative) {
        setSelectedCreativeId(initialCreative.creative.creativeID || '');
        setCreativeName(initialCreative.creative.creativeName || '');

        // Extract selected metrics (those with column - from data source)
        const metricsWithColumn =
          initialCreative.creative.metrics
            ?.filter((m): m is NonNullable<typeof m> => m !== null && !!m.column)
            .map((m) => m.column || '')
            .filter(Boolean) || [];
        setSelectedMetrics(metricsWithColumn);

        // Load availableMetricsObjects (metrics with column)
        const metricsObjects =
          initialCreative.creative.metrics
            ?.filter((m): m is NonNullable<typeof m> => m !== null && !!m.column)
            .map((m, index) => ({
              position: index + 1,
              label: m.label || '',
              value: m.value || 0,
              column: m.column || '',
              decimals: m.decimals ?? 2,
            })) || [];
        setAvailableMetricsObjects(metricsObjects);

        // Load custom metrics (custom metrics have no column, only label and value)
        const customMets =
          initialCreative.creative.metrics
            ?.filter((m): m is NonNullable<typeof m> => m !== null && !m.column)
            .map((m) => ({
              label: m.label || '',
              value: m.value || 0,
              decimals: m.decimals ?? 2,
            })) || [];
        setCustomMetrics(customMets);

        setStep('creative');
      }
      if (initialCreative.dataSourceName) {
        const ds = dataSources.find((d) => d.name === initialCreative.dataSourceName);
        if (ds) {
          setSelectedDataSource(ds);

          // If we don't have data, auto-fetch it
          if (!initialCreative.data || initialCreative.data.length === 0) {
            setIsLoading(true);
            const processedDataSource = processDataSource(ds);
            const q = prepQuery(
              processedDataSource,
              true,
              transformedACDetailsReport,
              user,
              customReportCampaignList,
              reportDateFilter,
            );
            setUserQuery(q);
          }
        }
      }
    }
  }, [
    modal,
    initialCreative,
    dataSources,
    transformedACDetailsReport,
    user,
    customReportCampaignList,
    reportDateFilter,
  ]);

  // Effect: Load presigned URL for file preview
  useEffect(() => {
    const loadFilePreview = async () => {
      const outputLocation = initialCreative?.creative?.creativeOutputLocation;
      if (!outputLocation) return;

      // Skip if already a blob URL (local file)
      if (outputLocation.startsWith('blob:') || outputLocation.startsWith('data:')) {
        setFilePreviewUrl(outputLocation);
        return;
      }

      try {
        const presignedResponse = (await API.graphql({
          query: getPreSignedUrl,
          variables: {
            ClientMethod: S3Method.get_object,
            Client: {
              code: activeCustomReport?.Client?.Code,
              country: activeCustomReport?.Client?.Country,
            },
            Environment: getEnvironment,
            FileName: outputLocation,
          },
          authMode: 'AMAZON_COGNITO_USER_POOLS',
        })) as { data: GetPreSignedUrlQuery };

        const url = presignedResponse.data.getPreSignedUrl?.url;
        if (url) {
          setFilePreviewUrl(url);
        }
      } catch (error) {
        console.error('Error loading file preview:', error);
      }
    };

    loadFilePreview();
  }, [
    initialCreative?.creative?.creativeOutputLocation,
    activeCustomReport?.Client?.Code,
    activeCustomReport?.Client?.Country,
    getEnvironment,
  ]);

  // Effect: Fetch creative from API when not in cache
  useEffect(() => {
    if (!initialCreative && layoutItem.SectionItemID && fetchCreative) {
      if (activeCustomReport?.authorized) {
        setIsLoading(true);
        fetchCreative(layoutItem.SectionItemID).finally(() => setIsLoading(false));
      } else {
        // For unauthorized users, first check if there's published data
        publishedData(layoutItem.SectionItemID).then((data) => {
          if (data) {
            // Published data exists, fetch the creative
            setIsLoading(true);
            fetchCreative(layoutItem.SectionItemID as string).finally(() => setIsLoading(false));
          } else {
            // No published data, remove item from layout
            safeSectionLayoutUpdate((prev) => {
              return prev.map((section) => {
                if (section.SectionID === sectionID) {
                  return {
                    ...section,
                    SectionElements: section.SectionElements?.filter(
                      (item) => item?.SectionItemID !== layoutItem.SectionItemID,
                    ),
                  };
                }
                return section;
              });
            });
          }
        });
      }
    }
  }, [
    layoutItem.SectionItemID,
    initialCreative,
    fetchCreative,
    activeCustomReport?.authorized,
    publishedData,
    safeSectionLayoutUpdate,
    sectionID,
  ]);

  // Handler: Fetch data for selected data source
  const handleFetchData = () => {
    if (!selectedDataSource) return;

    setIsLoading(true);
    const processedDataSource = processDataSource(selectedDataSource);
    const q = prepQuery(
      processedDataSource,
      true,
      transformedACDetailsReport,
      user,
      customReportCampaignList,
      reportDateFilter,
    );
    setUserQuery(q);
  };

  // Handler: Single select toggle
  const handleSingleSelectToggle = (selectedId: string) => {
    const newId = selectedCreativeId === selectedId ? '' : selectedId;
    setSelectedCreativeId(newId);
    // Clear metrics when creative ID changes
    if (newId !== selectedCreativeId) {
      setSelectedMetrics([]);
      setAvailableMetricsObjects([]);
    }
  };

  // Handler: Multi select toggle
  const handleMultiSelectToggle = (selectedId: string) => {
    setSelectedCreativeIds((prev) => {
      const newIds = prev.includes(selectedId) ? prev.filter((id) => id !== selectedId) : [...prev, selectedId];

      // Clear metrics when creative IDs change
      setSelectedMetrics([]);
      setAvailableMetricsObjects([]);

      return newIds;
    });
  };

  // Handler: Toggle select mode (single/multi)
  const toggleSelectMode = () => {
    setIsMultiSelect(!isMultiSelect);
    // Clear selections when switching modes
    setSelectedCreativeId('');
    setSelectedCreativeIds([]);
    setCustomCreativeName('');
  };

  // Handler: Toggle metric selection (like AddCreativeModal)
  const handleMetricToggle = (metricId: string) => {
    const isCurrentlySelected = selectedMetrics.includes(metricId);

    if (isCurrentlySelected) {
      // Remove from both selectedMetrics and availableMetricsObjects
      setSelectedMetrics(selectedMetrics.filter((id) => id !== metricId));
      setAvailableMetricsObjects(availableMetricsObjects.filter((obj) => obj.column !== metricId));
    } else if (selectedMetrics.length + customMetrics.length < 4) {
      // Add to selectedMetrics
      setSelectedMetrics([...selectedMetrics, metricId]);

      // Create complete metrics object with data
      const metricValue = getMetricValue(metricId);
      const newMetricObject = {
        position: selectedMetrics.length + 1,
        label: metricId.charAt(0).toUpperCase() + metricId.slice(1),
        value: metricValue,
        column: metricId,
        decimals: 2,
      };

      setAvailableMetricsObjects([...availableMetricsObjects, newMetricObject]);
    }
  };

  // Handler: Add custom metric
  const handleAddCustomMetric = () => {
    if (customLabel && customValue && availableMetricsObjects.length + customMetrics.length < 4) {
      setCustomMetrics([
        ...customMetrics,
        {
          label: customLabel,
          value: parseFloat(customValue) || 0,
          decimals: 2,
        },
      ]);
      setCustomLabel('');
      setCustomValue('');
      setShowCustomModal(false);
    }
  };

  // Handler: Edit data metric
  const handleEditDataMetric = (metricId: string) => {
    const availableMetric = availableMetricsObjects.find((m) => m.column === metricId);
    if (availableMetric) {
      setEditingMetric({
        type: 'data',
        metricId,
        label: availableMetric.label,
        value: availableMetric.value.toString(),
        decimals: availableMetric.decimals,
      });
      setEditLabel(availableMetric.label);
      setEditValue(availableMetric.value.toString());
      setEditDecimals(availableMetric.decimals.toString());
      setShowEditModal(true);
    }
  };

  // Handler: Edit custom metric
  const handleEditCustomMetric = (index: number) => {
    const customMetric = customMetrics[index];
    if (customMetric) {
      setEditingMetric({
        type: 'custom',
        index,
        label: customMetric.label,
        value: customMetric.value.toString(),
        decimals: customMetric.decimals || 2,
      });
      setEditLabel(customMetric.label);
      setEditValue(customMetric.value.toString());
      setEditDecimals((customMetric.decimals || 2).toString());
      setShowEditModal(true);
    }
  };

  // Handler: Save edit
  const handleSaveEdit = () => {
    if (!editingMetric || !editLabel || !editValue) return;

    const newDecimals = parseInt(editDecimals) || 2;

    if (editingMetric.type === 'custom' && editingMetric.index !== undefined) {
      const updatedCustomMetrics = [...customMetrics];
      updatedCustomMetrics[editingMetric.index] = {
        label: editLabel,
        value: parseFloat(editValue) || 0,
        decimals: newDecimals,
      };
      setCustomMetrics(updatedCustomMetrics);
    } else if (editingMetric.type === 'data' && editingMetric.metricId) {
      const originalMetric = availableMetricsObjects.find((obj) => obj.column === editingMetric.metricId);
      const originalValue = originalMetric?.value || parseFloat(editingMetric.value) || 0;
      const newValue = parseFloat(editValue) || 0;

      // If only LABEL or DECIMALS changed, keep as available metric
      if (newValue === originalValue) {
        setAvailableMetricsObjects(
          availableMetricsObjects.map((obj) =>
            obj.column === editingMetric.metricId ? { ...obj, label: editLabel, decimals: newDecimals } : obj,
          ),
        );
      } else {
        // If VALUE changed, convert to custom metric
        setSelectedMetrics(selectedMetrics.filter((id) => id !== editingMetric.metricId));
        setAvailableMetricsObjects(availableMetricsObjects.filter((obj) => obj.column !== editingMetric.metricId));
        setCustomMetrics([
          ...customMetrics,
          {
            label: editLabel,
            value: newValue,
            decimals: newDecimals,
          },
        ]);
      }
    }

    setEditingMetric(null);
    setEditLabel('');
    setEditValue('');
    setEditDecimals('2');
    setShowEditModal(false);
  };

  // Handler: Cancel edit
  const handleCancelEdit = () => {
    setEditingMetric(null);
    setEditLabel('');
    setEditValue('');
    setEditDecimals('2');
    setShowEditModal(false);
  };

  // Handler: Remove custom metric
  const handleRemoveCustomMetric = (index: number) => {
    setCustomMetrics(customMetrics.filter((_, i) => i !== index));
  };

  // Helper: Check if can add more metrics
  const getTotalMetricsCount = () => availableMetricsObjects.length + customMetrics.length;
  const canAddMoreMetrics = () => getTotalMetricsCount() < 4;

  // Handler: File upload
  const handleFileChange = (file: File) => {
    setSelectedFile(file);
    setFilePreviewUrl(URL.createObjectURL(file));
  };

  // Handler: Save creative
  const handleSave = async () => {
    // Determine final creative ID based on mode
    const finalCreativeId = isMultiSelect ? selectedCreativeIds.join(',') : selectedCreativeId;
    const finalCreativeName = isMultiSelect
      ? customCreativeName
      : creativeName || availableCreativeIds.find((c) => c.id === selectedCreativeId)?.label || selectedCreativeId;

    if (!finalCreativeId) {
      alert('Please select a Creative ID');
      return;
    }

    if (isMultiSelect && !customCreativeName.trim()) {
      alert('Please enter a custom creative name for multi-select');
      return;
    }

    if (!selectedDataSource?.name) {
      alert('Please select a Data Source');
      return;
    }

    setIsLoading(true);

    try {
      // Step 1: Get or create SectionItemID
      let sectionItemId = layoutItem.SectionItemID;

      if (!sectionItemId) {
        const gridLayoutInput: GridLayoutInput = {
          Position: layoutItem.Position,
          GridPosition: removeTypename(layoutItem.GridPosition),
          Type: 'creative',
          HasData: true,
        };

        const response = await saveNewSectionItem(sectionID as string, gridLayoutInput);

        if (response && response.status === 200 && response.id) {
          sectionItemId = response.id;
        } else {
          throw new Error(`Failed to create section item: ${response?.message}`);
        }
      }

      // Step 2: Upload file to S3 if new file selected
      let outputLocation = initialCreative?.creative?.creativeOutputLocation || '';

      if (selectedFile) {
        const fileExtension = selectedFile.name.split('.').pop();
        const fileName = `${uuidv4()}.${fileExtension}`;

        // Get presigned URL
        const presignedResponse = (await API.graphql({
          query: getPreSignedUrl,
          variables: {
            ClientMethod: S3Method.put_object,
            Client: {
              code: activeCustomReport?.Client?.Code,
              country: activeCustomReport?.Client?.Country,
            },
            Environment: getEnvironment,
            FileName: fileName,
            ContentType: selectedFile.type,
          },
          authMode: 'AMAZON_COGNITO_USER_POOLS',
        })) as { data: GetPreSignedUrlQuery };

        const presignedUrlData = presignedResponse.data.getPreSignedUrl;

        if (presignedUrlData?.url) {
          // Upload file
          await fetch(presignedUrlData.url, {
            method: 'PUT',
            body: selectedFile,
            headers: {
              'Content-Type': selectedFile.type,
            },
          });
          outputLocation = fileName;
        }
      }

      // Step 3: Prepare metrics (combine available metrics objects + custom metrics)
      const metricsInput: CreativeMetricsInput[] = [
        // Add available metrics objects (data metrics with column reference)
        ...availableMetricsObjects.map(
          (metricObj, index): CreativeMetricsInput => ({
            position: index + 1,
            label: metricObj.label,
            value: metricObj.value,
            column: metricObj.column,
            decimals: metricObj.decimals,
          }),
        ),
        // Add custom metrics (no column reference)
        ...customMetrics.map(
          (customMetric, index): CreativeMetricsInput => ({
            position: availableMetricsObjects.length + index + 1,
            label: customMetric.label,
            value: customMetric.value,
            column: undefined, // Custom metrics don't have column reference
            decimals: customMetric.decimals || 2,
          }),
        ),
      ];

      // Step 4: Save using storeQueriesV2
      const creativeInput: CreativeInput = {
        id: `${sectionItemId}.0`,
        position: 0,
        creativeOutputLocation: outputLocation,
        creativeID: finalCreativeId,
        creativeName: finalCreativeName,
        metrics: metricsInput,
      };

      const result = await saveCreative(
        sectionItemId,
        selectedDataSource.name,
        [creativeInput],
        creativeTitle || undefined,
      );

      if (result === 'success') {
        // Build creative values for cache
        const creativeValues: CreativeValues = {
          dataSourceName: selectedDataSource?.name,
          title: creativeTitle,
          creative: {
            __typename: 'Creative',
            id: creativeInput.id,
            position: creativeInput.position,
            creativeOutputLocation: outputLocation,
            creativeID: finalCreativeId,
            creativeName: finalCreativeName,
            metrics: metricsInput.map((m) => ({
              __typename: 'CreativeMetricsResponse' as const,
              position: m.position,
              label: m.label,
              value: m.value,
              column: m.column,
              decimals: m.decimals,
            })),
          },
          data: fetchedData,
          outputLocation: dataOutputLocation,
        };

        // IMPORTANT: Update cache FIRST, then close modal
        // This ensures the grid component gets the updated data when it re-renders
        onCreativeSaved?.(sectionItemId, creativeValues);

        // Update section layout (this closes the modal)
        safeSectionLayoutUpdate((prevState) => {
          return prevState.map((section) => {
            return {
              ...section,
              SectionElements: section.SectionElements?.map((el) => {
                if (el?.SectionItemID === sectionItemId || el?.GridPosition?.i === layoutItem.GridPosition?.i) {
                  return {
                    ...el,
                    SectionItemID: sectionItemId,
                    Type: 'creative',
                    HasData: true,
                    Open: false,
                    __typename: 'GridLayout',
                  } as GridLayout;
                }
                return el;
              }),
            };
          });
        });
      } else {
        throw new Error('Failed to save creative');
      }
    } catch (error) {
      console.error('Error saving creative:', error);
      alert(`Error saving creative: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Render: File preview
  const renderPreview = (url: string) => {
    if (!url) return null;

    // Extract file extension - handle both direct URLs and presigned URLs with query params
    const urlWithoutParams = url.split('?')[0];
    const outputLocation = initialCreative?.creative?.creativeOutputLocation || '';

    // Check extension from URL, output location, or selected file type
    const isImage =
      urlWithoutParams.match(/\.(jpg|jpeg|png|gif|webp)$/i) ||
      outputLocation.match(/\.(jpg|jpeg|png|gif|webp)$/i) ||
      selectedFile?.type?.startsWith('image/');

    const isVideo =
      urlWithoutParams.match(/\.(mp4|webm|mov|avi)$/i) ||
      outputLocation.match(/\.(mp4|webm|mov|avi)$/i) ||
      selectedFile?.type?.startsWith('video/');

    if (isImage) {
      return <img src={url} alt="Preview" />;
    }
    if (isVideo) {
      return <video src={url} controls />;
    }
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <span className="material-symbols-outlined" style={{ fontSize: 48, color: '#999' }}>
          description
        </span>
        <p>File selected</p>
      </div>
    );
  };

  // ============ INLINE DISPLAY MODE ============
  if (!modal) {
    const creative = initialCreative?.creative;

    return (
      <CreativeDisplayStyled>
        {userIsAuthorized && (
          <div className="edit-btns">
            <div
              onClick={(e) => {
                e.stopPropagation();
                handleModalType(layoutItem, 'creative');
              }}
              className="edit-btn icon material-symbols-outlined"
            >
              edit
            </div>
            <div className="edit-btn icon material-symbols-outlined" onClick={() => setShowDeleteModal(true)}>
              delete
            </div>
          </div>
        )}

        {creative ? (
          <div className="creative-content">
            {initialCreative?.title && (
              <h1
                className="title"
                style={{
                  fontSize: '14px',
                  margin: '0',
                  padding: '8px 10px 4px',
                  textAlign: 'left',
                  width: '100%',
                  fontFamily: 'fontRegular',
                  color: theme.primaryGray,
                  flexShrink: 0,
                }}
              >
                {initialCreative.title}
              </h1>
            )}
            <div className="preview-container" style={{ marginTop: initialCreative?.title ? '0' : undefined }}>
              {filePreviewUrl ? (
                renderPreview(filePreviewUrl)
              ) : (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px' }}>
                  <ReactLoading type="spinningBubbles" color={theme.publishButton} height={40} width={40} />
                </div>
              )}
            </div>
            {creative.creativeName && <div className="creative-name">{creative.creativeName}</div>}
            {creative.metrics && creative.metrics.length > 0 && (
              <div className="metrics-row">
                {creative.metrics
                  .filter((m): m is NonNullable<typeof m> => m !== null)
                  .map((metric, idx) => (
                    <div key={idx} className="metric">
                      <span className="label">{metric.label}</span>
                      <span className="value">
                        {(metric.value || 0).toLocaleString(undefined, {
                          minimumFractionDigits: metric.decimals ?? 2,
                          maximumFractionDigits: metric.decimals ?? 2,
                        })}
                      </span>
                    </div>
                  ))}
              </div>
            )}
          </div>
        ) : (
          <div className="empty-state">No creative data</div>
        )}

        {showDeleteModal &&
          createPortal(
            <DeleteConfirmationModal
              handleDeleteConfirmationModal={(confirmed) => {
                if (confirmed) {
                  deleteItem?.(sectionID as string, layoutItem.SectionItemID as string);
                }
                setShowDeleteModal(false);
              }}
              modalObject={{
                title: 'Delete Creative',
                message: 'Are you sure you want to delete this creative?',
                mainButton: 'Delete',
                secondaryButton: 'Cancel',
              }}
            />,
            document.body,
          )}
      </CreativeDisplayStyled>
    );
  }

  // ============ MODAL MODE ============
  return (
    <ModalContentStyled>
      {/* STEP 1: Data Source Selection */}
      {step === 'dataSource' && (
        <div className="step-container">
          {isLoading ? (
            <div className="loading-state">
              <span>Loading data...</span>
            </div>
          ) : (
            <>
              <PlatformMidRow isGraph={false}>
                <h1>Data Source</h1>
                <h2>Choose a data source for your creative</h2>
                <div className="platformsArray">
                  {filteredDataSources.map((platform) => (
                    <label key={platform.name}>
                      <input
                        type="radio"
                        name="platforms"
                        value={platform.name}
                        checked={selectedDataSource?.name === platform.name}
                        onChange={() => setSelectedDataSource(platform)}
                      />
                      <div className="checked">
                        <span style={{ fontSize: '1.5rem', marginBottom: '-5px' }}>
                          {platformLogoSelector(platform.name)}
                        </span>
                        <span>{platform.name}</span>
                      </div>
                    </label>
                  ))}
                </div>
              </PlatformMidRow>

              <BottomRow>
                <button type="button" onClick={() => handleModalType(null, null)}>
                  Cancel
                </button>
                <DarkButton onClick={handleFetchData} disabled={!selectedDataSource}>
                  Fetch Data
                </DarkButton>
              </BottomRow>
            </>
          )}
        </div>
      )}

      {/* STEP 2: Creative Editor - 3 Column Layout (identical to AddCreativeModal) */}
      {step === 'creative' && (
        <div className="step-container">
          {isLoading ? (
            <div
              className="loading-state"
              style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}
            >
              <ReactLoading type="spinningBubbles" color={theme.publishButton} height={50} width={50} />
            </div>
          ) : (
            <>
              {/* Title Input - above 3 column layout */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  marginBottom: '1rem',
                  paddingBottom: '0.75rem',
                  borderBottom: `1px solid ${theme.border}20`,
                }}
              >
                <label
                  style={{
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    color: theme.secondaryGray,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Title
                </label>
                <input
                  type="text"
                  value={creativeTitle}
                  onChange={(e) => setCreativeTitle(e.target.value)}
                  placeholder="Optional title..."
                  style={{
                    flex: 1,
                    maxWidth: '280px',
                    padding: '0.5rem 0.75rem',
                    fontSize: '0.8rem',
                    border: `1px solid ${theme.border}`,
                    borderRadius: '6px',
                    backgroundColor: theme.placeholderBackground,
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                  }}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'row', gap: '2rem', width: '100%' }}>
                {/* Column 1: Upload File */}
                <MidRow
                  style={{
                    flex: '1',
                    borderRight: `1px solid ${theme.border}`,
                    minHeight: '300px',
                    padding: '0 1rem',
                  }}
                >
                  <h2>1. Upload Creative</h2>
                  <FileUploader
                    handleChange={handleFileChange}
                    name="file"
                    types={['JPG', 'PNG', 'MP4', 'MOV', 'AVI', 'HTML', 'HTM']}
                  >
                    <div
                      style={{
                        border: `1px dashed ${theme.border}`,
                        borderRadius: '4px',
                        padding: '1rem',
                        textAlign: 'center',
                        cursor: 'pointer',
                        backgroundColor: theme.placeholderBackground,
                        fontSize: '0.8rem',
                      }}
                    >
                      {selectedFile ? (
                        <div>
                          <div style={{ marginBottom: '0.25rem' }}>✓ New File Selected</div>
                          <div style={{ color: theme.primaryGray, fontSize: '0.7rem' }}>{selectedFile.name}</div>
                        </div>
                      ) : filePreviewUrl ? (
                        <div>
                          <div style={{ marginBottom: '0.25rem' }}>📁 Current File</div>
                          <div style={{ color: theme.primaryGray, fontSize: '0.6rem', marginTop: '0.5rem' }}>
                            Click to upload new file
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div style={{ marginBottom: '0.25rem' }}>Add File</div>
                          <div style={{ color: theme.primaryGray, fontSize: '0.7rem' }}>
                            JPG, PNG, MP4, MOV, AVI, HTML
                          </div>
                        </div>
                      )}
                    </div>
                  </FileUploader>

                  {filePreviewUrl && (
                    <div
                      style={{
                        marginTop: '0.5rem',
                        border: `1px solid ${theme.border}`,
                        borderRadius: '4px',
                        padding: '0.25rem',
                        backgroundColor: theme.placeholderBackground,
                        maxHeight: '80px',
                        maxWidth: '120px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                      }}
                    >
                      {renderPreview(filePreviewUrl)}
                    </div>
                  )}
                </MidRow>

                {/* Column 2: Select Creative ID (identical to AddCreativeModal) */}
                <MidRow style={{ flex: '1', borderRight: `1px solid ${theme.border}`, padding: '0 1rem' }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '0.5rem',
                    }}
                  >
                    <h2>2. Select Creative ID</h2>
                    <button
                      type="button"
                      onClick={toggleSelectMode}
                      style={{
                        fontSize: '0.7rem',
                        backgroundColor: isMultiSelect ? theme.primaryGray : 'transparent',
                        color: isMultiSelect ? 'white' : theme.primaryGray,
                        boxShadow: isMultiSelect ? 'none' : 'inherit',
                        cursor: 'pointer',
                      }}
                    >
                      {isMultiSelect ? 'Multi' : 'Single'}
                    </button>
                  </div>

                  {isMultiSelect && (
                    <div
                      style={{
                        marginBottom: '0.5rem',
                        padding: '0.5rem',
                        backgroundColor: theme.placeholderBackground || '#f8f9fa',
                        borderRadius: '4px',
                        border: `1px solid ${theme.border}`,
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: '0.5rem',
                      }}
                    >
                      <label
                        style={{
                          fontSize: '0.8rem',
                          color: theme.primaryGray,
                          display: 'block',
                          marginBottom: '0.25rem',
                        }}
                      >
                        Custom Creative Name:
                      </label>
                      <input
                        type="text"
                        value={customCreativeName}
                        onChange={(e) => setCustomCreativeName(e.target.value)}
                        placeholder="Enter custom name..."
                        style={{
                          display: 'block',
                          flex: '1',
                        }}
                      />
                    </div>
                  )}
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.25rem',
                      marginBottom: '0.5rem',
                      maxHeight: '200px',
                      overflowY: 'auto',
                    }}
                  >
                    {availableCreativeIds.map((creative) => {
                      const isSelected = isMultiSelect
                        ? selectedCreativeIds.includes(creative.id)
                        : selectedCreativeId === creative.id;

                      return (
                        <Selections
                          key={creative.id}
                          onClick={() =>
                            isMultiSelect ? handleMultiSelectToggle(creative.id) : handleSingleSelectToggle(creative.id)
                          }
                          style={{
                            backgroundColor: isSelected ? theme.primaryGray : 'transparent',
                            color: isSelected ? 'white' : theme.primaryGray,
                            cursor: 'pointer',
                            textAlign: 'left',
                            margin: '0',
                            width: '100%',
                            position: 'relative',
                          }}
                        >
                          <div style={{ display: 'flex', flexDirection: 'column', padding: '0.25rem' }}>
                            <span style={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{creative.label}</span>
                            <span style={{ fontSize: '0.7rem', opacity: 0.8 }} title={creative.label}>
                              ID: {creative.id}
                            </span>
                          </div>
                          {isMultiSelect && isSelected && (
                            <span
                              style={{
                                position: 'absolute',
                                right: '0.5rem',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                fontSize: '0.8rem',
                              }}
                              className="material-symbols-outlined"
                            >
                              check
                            </span>
                          )}
                        </Selections>
                      );
                    })}
                  </div>
                  <div style={{ fontSize: '0.7rem', color: theme.primaryGray, textAlign: 'center' }}>
                    {isMultiSelect
                      ? `${selectedCreativeIds.length} selected${customCreativeName ? ` - "${customCreativeName}"` : ' (name required)'}`
                      : selectedCreativeId
                        ? '1 selected'
                        : '0 selected'}
                  </div>
                </MidRow>

                {/* Column 3: Select Metrics (identical to AddCreativeModal) */}
                <MidRow style={{ flex: '1', minHeight: '400px', padding: '0 1rem' }}>
                  <h2>3. Select up to 4 Metrics</h2>

                  {/* Selected Metrics (shown when metrics are selected) */}
                  {(selectedMetrics.length > 0 || customMetrics.length > 0) && (
                    <div style={{ marginBottom: '1rem' }}>
                      <h2>Selected Metrics ({selectedMetrics.length + customMetrics.length}/4):</h2>
                      <RightPane style={{ border: 'none', padding: 0 }}>
                        {/* Selected data metrics */}
                        <div className="listContainer">
                          {selectedMetrics.map((metricId) => {
                            const metricObj = availableMetricsObjects.find((m) => m.column === metricId);
                            return (
                              <Selections
                                key={metricId}
                                chosen={true}
                                style={{
                                  position: 'relative',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                }}
                              >
                                <span>{metricObj?.label || metricId}</span>
                                <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
                                  <span
                                    style={{
                                      cursor: 'pointer',
                                      fontSize: '12px',
                                      padding: '2px 4px',
                                      opacity: 0.7,
                                    }}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleEditDataMetric(metricId);
                                    }}
                                    title="Edit metric"
                                    className="material-symbols-outlined"
                                  >
                                    edit
                                  </span>
                                  <span
                                    style={{
                                      cursor: 'pointer',
                                      fontSize: '14px',
                                      padding: '2px 4px',
                                      opacity: 0.7,
                                    }}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleMetricToggle(metricId);
                                    }}
                                    title="Remove metric"
                                    className="material-symbols-outlined"
                                  >
                                    close
                                  </span>
                                </div>
                              </Selections>
                            );
                          })}

                          {/* Selected custom metrics */}
                          {customMetrics.map((metric, index) => (
                            <Selections
                              key={`custom-${index}`}
                              chosen={true}
                              style={{
                                position: 'relative',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                              }}
                            >
                              <span>
                                {metric.label}: {metric.value}
                              </span>
                              <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
                                <span
                                  style={{
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                    padding: '2px 4px',
                                    opacity: 0.7,
                                  }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleEditCustomMetric(index);
                                  }}
                                  title="Edit custom metric"
                                  className="material-symbols-outlined"
                                >
                                  edit
                                </span>
                                <span
                                  style={{
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    padding: '2px 4px',
                                    opacity: 0.7,
                                  }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleRemoveCustomMetric(index);
                                  }}
                                  title="Remove custom metric"
                                  className="material-symbols-outlined"
                                >
                                  close
                                </span>
                              </div>
                            </Selections>
                          ))}
                        </div>
                      </RightPane>
                    </div>
                  )}

                  {/* Available Metrics from Data */}
                  <div style={{ marginBottom: '1rem' }}>
                    <h2>Available Metrics:</h2>
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '0.25rem',
                        maxHeight: '150px',
                        overflowY: 'auto',
                      }}
                    >
                      {availableMetrics.map((metric) => (
                        <Selections
                          key={metric}
                          onClick={() => handleMetricToggle(metric)}
                          chosen={selectedMetrics.includes(metric)}
                          style={{
                            opacity: !selectedMetrics.includes(metric) && !canAddMoreMetrics() ? 0.5 : 1,
                            backgroundColor: selectedMetrics.includes(metric) ? theme.primaryGray : 'transparent',
                            color: selectedMetrics.includes(metric) ? 'white' : theme.primaryGray,
                            cursor:
                              !selectedMetrics.includes(metric) && !canAddMoreMetrics() ? 'not-allowed' : 'pointer',
                          }}
                        >
                          <span title={metric}>{metric}</span>
                        </Selections>
                      ))}
                    </div>
                  </div>

                  {/* Custom Metrics */}
                  <RightPane style={{ border: 'none', padding: 0, marginBottom: '1rem' }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '0.5rem',
                      }}
                    >
                      <h2 style={{ margin: 0, fontSize: '0.9rem' }}>Custom Metrics:</h2>
                      {canAddMoreMetrics() && (
                        <Selections
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowCustomModal(true);
                          }}
                          style={{
                            fontSize: '0.7rem',
                            padding: '0.25rem 0.5rem',
                            backgroundColor: 'transparent',
                            color: theme.primaryGray,
                            cursor: 'pointer',
                            border: `1px solid ${theme.border}`,
                            textAlign: 'center',
                            margin: 0,
                          }}
                        >
                          + Add Custom
                        </Selections>
                      )}
                    </div>
                    <div className="listContainer">
                      {customMetrics.map((customMetric, index) => (
                        <Selections
                          key={`custom-list-${index}`}
                          chosen={true}
                          style={{
                            position: 'relative',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <span>
                            {customMetric.label}: {customMetric.value}
                          </span>
                          <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
                            <span
                              style={{
                                cursor: 'pointer',
                                fontSize: '12px',
                                padding: '2px 4px',
                                opacity: 0.7,
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleEditCustomMetric(index);
                              }}
                              title="Edit custom metric"
                              className="material-symbols-outlined"
                            >
                              edit
                            </span>
                            <span
                              style={{
                                cursor: 'pointer',
                                fontSize: '14px',
                                padding: '2px 4px',
                                opacity: 0.7,
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleRemoveCustomMetric(index);
                              }}
                              title="Remove custom metric"
                              className="material-symbols-outlined"
                            >
                              close
                            </span>
                          </div>
                        </Selections>
                      ))}
                    </div>
                  </RightPane>

                  {/* Summary */}
                  <div style={{ fontSize: '0.7rem', color: theme.primaryGray, textAlign: 'center' }}>
                    {getTotalMetricsCount()}/4 selected ({availableMetricsObjects.length} data + {customMetrics.length}{' '}
                    custom)
                  </div>
                </MidRow>
              </div>

              <BottomRow style={{ marginTop: '2rem', borderTop: `1px solid ${theme.border}`, paddingTop: '1rem' }}>
                <NavigationButton
                  type="button"
                  style={{ backgroundColor: 'transparent' }}
                  onClick={() => setStep('dataSource')}
                >
                  Back
                </NavigationButton>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <NavigationButton type="button" onClick={() => handleModalType(null, null)}>
                    Cancel
                  </NavigationButton>
                  <DarkButton
                    onClick={handleSave}
                    disabled={
                      (isMultiSelect
                        ? selectedCreativeIds.length === 0 || !customCreativeName.trim()
                        : !selectedCreativeId) ||
                      (!isEditMode && !selectedFile) ||
                      (availableMetricsObjects.length === 0 && customMetrics.length === 0)
                    }
                    style={{
                      opacity:
                        (isMultiSelect
                          ? selectedCreativeIds.length > 0 && customCreativeName.trim()
                          : selectedCreativeId) &&
                        (isEditMode || selectedFile) &&
                        (availableMetricsObjects.length > 0 || customMetrics.length > 0)
                          ? 1
                          : 0.5,
                    }}
                  >
                    {isEditMode ? 'Update Creative' : 'Save Creative'}
                  </DarkButton>
                </div>
              </BottomRow>
            </>
          )}
        </div>
      )}

      {/* Custom Metric Modal - Separate modal (identical to AddCreativeModal) */}
      {showCustomModal && (
        <Modal>
          <HeadModul>
            <button
              type="button"
              className="x"
              onClick={() => {
                setShowCustomModal(false);
                setCustomLabel('');
                setCustomValue('');
              }}
            >
              <IoMdClose className="closeIcon" />
            </button>
            <ModulContainer style={{ maxWidth: '400px' }}>
              <h1>Add Custom Metric</h1>
              <div className="editDataForm">
                <h1>
                  Metric Label
                  <input
                    type="text"
                    value={customLabel}
                    onChange={(e) => setCustomLabel(e.target.value)}
                    placeholder="e.g., Custom CTR"
                  />
                </h1>
                <h1>
                  Metric Value
                  <input
                    type="number"
                    step="0.01"
                    value={customValue}
                    onChange={(e) => setCustomValue(e.target.value)}
                    placeholder="e.g., 2.45"
                  />
                </h1>
              </div>

              <div style={{ display: 'flex', marginTop: '10px', gap: '10px', marginLeft: 'auto', marginRight: '10px' }}>
                <IconButton
                  type="button"
                  onClick={handleAddCustomMetric}
                  disabled={!customLabel || !customValue}
                  style={{
                    opacity: customLabel && customValue ? 1 : 0.5,
                  }}
                >
                  Save
                </IconButton>
                <IconButton
                  type="button"
                  onClick={() => {
                    setShowCustomModal(false);
                    setCustomLabel('');
                    setCustomValue('');
                  }}
                >
                  Cancel
                </IconButton>
              </div>
            </ModulContainer>
          </HeadModul>
        </Modal>
      )}

      {/* Edit Metric Modal - Separate modal (identical to AddCreativeModal) */}
      {showEditModal && editingMetric && (
        <Modal>
          <HeadModul>
            <button type="button" className="x" onClick={handleCancelEdit}>
              <IoMdClose className="closeIcon" />
            </button>
            <ModulContainer style={{ maxWidth: '400px' }}>
              <h1>Edit Metric</h1>
              <div className="editDataForm">
                <h1>
                  Metric Label
                  <input
                    type="text"
                    value={editLabel}
                    onChange={(e) => setEditLabel(e.target.value)}
                    placeholder="e.g., Custom CTR"
                  />
                </h1>
                <h1>
                  Metric Value{editingMetric.type === 'data' ? ' *' : ''}
                  <input
                    type="number"
                    step="0.01"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    placeholder="e.g., 2.45"
                  />
                </h1>
                <h1>
                  Decimal Places
                  <input
                    type="number"
                    min="0"
                    max="10"
                    step="1"
                    value={editDecimals}
                    onChange={(e) => setEditDecimals(e.target.value)}
                    placeholder="e.g., 2"
                  />
                </h1>
              </div>

              {editingMetric.type === 'data' && (
                <small
                  style={{
                    fontSize: '0.75rem',
                    color: '#666',
                    display: 'block',
                    marginTop: '10px',
                    marginBottom: '10px',
                    fontStyle: 'italic',
                    textAlign: 'left',
                  }}
                >
                  * Changing this value will convert the metric to a custom metric and it will no longer update
                  automatically from data-lake
                </small>
              )}

              <div style={{ display: 'flex', marginTop: '10px', gap: '10px', marginLeft: 'auto', marginRight: '10px' }}>
                <IconButton
                  type="button"
                  onClick={handleSaveEdit}
                  disabled={!editLabel || !editValue}
                  style={{
                    opacity: editLabel && editValue ? 1 : 0.5,
                  }}
                >
                  Save
                </IconButton>
                <IconButton type="button" onClick={handleCancelEdit}>
                  Cancel
                </IconButton>
              </div>
            </ModulContainer>
          </HeadModul>
        </Modal>
      )}
    </ModalContentStyled>
  );
};

export default CustomReportCreative;
